import React, { useState, useEffect, useCallback } from 'react';
// Company edit form with proper hooks
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { logger } from '../../utils/logger';

interface CompanyDetails {
  company_id: string;
  company_name: string;
  display_name?: string;
  tier: string;
  status: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  users: Array<{
    user_id: string;
    email: string;
    role: string;
    is_active: boolean;
  }>;
}

const EditCompany: React.FC = () => {
  const navigate = useNavigate();
  const { companyId } = useParams<{ companyId: string }>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [company, setCompany] = useState<CompanyDetails | null>(null);
  const [formData, setFormData] = useState({
    company_name: '',
    display_name: '',
    tier: 'free',
    status: 'active',
  });

  const fetchCompanyDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get<CompanyDetails>(`/admin/companies/${companyId}`);

      setCompany(response.data);
      setFormData({
        company_name: response.data.company_name,
        display_name: response.data.display_name || '',
        tier: response.data.tier,
        status: response.data.status,
      });
      setError(null);
    } catch (err: any) {
      logger.error('Fetch company error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to fetch company details';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      fetchCompanyDetails();
    }
  }, [companyId, fetchCompanyDetails]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSelectChange = (field: string) => (e: any) => {
    setFormData({
      ...formData,
      [field]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      await api.patch(`/admin/companies/${companyId}`, formData);

      setSuccess(true);
      setTimeout(() => {
        navigate('/admin/companies');
      }, 1500);
    } catch (err: any) {
      logger.error('Update company error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to update company';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!company) {
    return (
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Alert severity="error">Kompanija nije pronađena</Alert>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin/companies')}
          sx={{ mt: 2 }}
        >
          Nazad na Listu
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <BusinessIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
        <Typography variant="h4" component="h1">
          Ažuriraj Kompaniju
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Kompanija uspešno ažurirana! Preusmeravanje...
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>ID Kompanije:</strong> {company.company_id}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
              <strong>Kreirano:</strong> {new Date(company.created_at).toLocaleDateString('sr-RS')}
            </Typography>

            <TextField
              fullWidth
              label="Naziv Kompanije"
              name="company_name"
              value={formData.company_name}
              onChange={handleInputChange}
              required
              margin="normal"
            />

            <TextField
              fullWidth
              label="Prikazani Naziv (opciono)"
              name="display_name"
              value={formData.display_name}
              onChange={handleInputChange}
              margin="normal"
              helperText="Naziv koji će biti prikazan korisnicima"
            />

            <FormControl fullWidth margin="normal">
              <InputLabel>Paket</InputLabel>
              <Select
                value={formData.tier}
                onChange={handleSelectChange('tier')}
                label="Paket"
              >
                <MenuItem value="free">Besplatno</MenuItem>
                <MenuItem value="basic">Osnovno</MenuItem>
                <MenuItem value="premium">Premium</MenuItem>
                <MenuItem value="enterprise">Korporativno</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth margin="normal">
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={handleSelectChange('status')}
                label="Status"
              >
                <MenuItem value="active">Aktivan</MenuItem>
                <MenuItem value="trial">Probni Period</MenuItem>
                <MenuItem value="suspended">Suspendovan</MenuItem>
                <MenuItem value="cancelled">Otkazan</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Korisnici ({company.users.length})
            </Typography>
            {company.users.map((user) => (
              <Box key={user.user_id} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>{user.email}</strong> - {user.role} {!user.is_active && '(Neaktivan)'}
                </Typography>
              </Box>
            ))}

            <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                disabled={saving}
              >
                {saving ? 'Čuvam...' : 'Sačuvaj Izmene'}
              </Button>

              <Button
                variant="outlined"
                size="large"
                startIcon={<BackIcon />}
                onClick={() => navigate('/admin/companies')}
                disabled={saving}
              >
                Otkaži
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditCompany;
